/* Bodi Kit Builder — the Starter Kit page.
   Pick a case, pick your scents, pick a plan. The scene on the left is built
   live: a case is ALWAYS on stage (Blue on load — the ghost/empty state does
   not exist) and every REFILL, not every scent, drops a scent cluster into its
   own slot on a ring around it. Three Bloom is three Bloom clusters.

   All product data — variants, prices, the subscription selling plan, the
   scent metaobjects and the case cut-outs — arrives from Liquid in the
   [data-bkb-data] island. Nothing about the catalogue is hard-coded here.

   Cart contract (every line carries properties._bundle = 'Starter Kit'):
     Subscribe & Save  one line per scent with `selling_plan`, PLUS the case as
                       its own line with NO selling plan (that is what keeps the
                       case a one-time charge), then the BXGY code is applied.
     One-off           the same lines with no selling plan and no code. */
(function () {
  if (customElements.get('bodi-kit-builder')) return;

  /* ---------- money ---------- */
  function formatMoney(cents, fmt) {
    var v = (cents / 100).toFixed(2);
    var parts = v.split('.');
    var withCommas = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + parts[1];
    var noDec = Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (fmt || '${{amount}}')
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + parts[1])
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDec)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDec)
      .replace(/\{\{\s*amount\s*\}\}/g, withCommas);
  }

  var WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
  var word = function (n) { return WORDS[n] || String(n); };
  var esc = function (s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); };

  var checkSVG =
    '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#144634" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 12.5 9.5 18 20 6.5"/></svg>';

  /* ============================================================
     THE BUILT SCENE — the cluster solver
     All of this is solved in "w-units": the stage is 100 wide and 100*AR tall,
     so a circle stays a circle and one set of numbers covers every screen.

     There is one cluster per REFILL. Three Bloom is three Bloom clusters in
     three different slots, not one with a badge on it.

     How deeply a cluster may tuck behind the case is the constraint that wins.
     In priority order the solver will:
       1. keep the bite — the depth a cluster reaches into the case silhouette,
          as a fraction of that cluster's own width — at BITE_MAX or under, and
          keep the refill pod itself clear of the case entirely. The botanicals'
          outer edge may slip behind; the pod may not;
       2. keep the bite non-zero: the ring is solved TIGHT rather than wide, and
          a hug pass then walks each cluster in along its own spoke until it
          touches, so they read as incorporated with the case rather than
          floating beside it;
       3. keep the cluster as big as that allows, and never let a count step up
          into bigger clusters than the count below it;
       4. no visible cluster-to-cluster overlap, and never leave the stage.
     Slots sit on an arc around the case. Both the arc's width and the ring
     radius are searched, and the size is binary-searched inside that, so a
     dozen refills degrades to smaller clusters on a wider arc instead of
     breaking. Nothing is hand-placed.
     ============================================================ */
  var SQUASH = 1.14;  /* the ring is an ellipse, taller than it is wide  */
  var RING_CY = 52;   /* ring centre, w-units down from the stage top    */
  var EDGE = 1.5;     /* fallback inset, used only before the first
                         measurement of the real page edges              */
  var ART = 0.93;     /* the burst fills ~93% of its box; the rest is
                         transparent padding and may cross the bound     */
  var BOX = 60;       /* == --cluster in the CSS; only ever scaled       */
  /* The VISIBLE case, in w-units — what the eye actually sees, not the square
     PNG that carries it. Derived from the .stage-case CSS (--case-top 13,
     --case-h 58, --case-fill .925, --case-pad-top .0425) and the alpha bounding
     box of the store's own cut-outs (the case fills .935 of the PNG height,
     starts .0375 down it, and is .46 of the PNG wide):
       box height  58 / .925              = 62.70
       box top     13 − 62.70 × .0425     = 10.34
       visible top 10.34 + 62.70 × .0375  = 12.69
       visible h   62.70 × .935           = 58.63
       visible w   62.70 × .46            = 28.84  →  AR 28.84 / 58.63 = .492
     Re-derive these if the CSS constants change or the photography is re-cut:
     the solver is only as honest about the bite as this model is. */
  var CASE_TOP = 12.69, CASE_H = 58.63, CASE_AR = 0.492;
  /* Two, three or four cases fan out from the centre and shrink a little so the
     group still clears the stage. GAP is a percentage of the case box width, so
     the cases overlap slightly and read as a set rather than a row of clones. */
  var FAN = { 1: { gap: 0, scale: 1 }, 2: { gap: 34, scale: 0.94 }, 3: { gap: 31, scale: 0.88 }, 4: { gap: 28, scale: 0.82 } };
  function fanOf(n) { return FAN[Math.max(1, Math.min(4, n))] || FAN[1]; }
  /* The VISIBLE pod inside a cluster, as fractions of the cluster box, derived
     the same way off .sat-pod (51% tall at 46% down, --pod-fill .535,
     --pod-cy .5225) and the pod PNG's own alpha box (.545 tall, .385 wide):
       img box     51 / .535 = 95.33 of the cluster
       visible h   95.33 × .545 = 51.95   ·   visible w  95.33 × .385 = 36.70
       centre      46% of the cluster, i.e. 4% above its middle */
  var POD_W = 0.367, POD_H = 0.5195, POD_DY = -0.04;
  var POD_HIDE = 0.02; /* the pod stays out in front of the case, full stop */
  /* the bite: how far a cluster reaches into the case silhouette, measured as a
     fraction of that cluster's own visible width. A small, deliberate kiss at
     the case's edge — not a tuck. */
  var BITE_MAX = 0.1;  /* hard ceiling: a tenth of a cluster, no more      */
  var BITE_AIM = 0.07; /* what the hug pass walks each cluster in to reach */

  /* The size we're after at each count, in w-units. A ceiling, not a target:
     the page's real edges are usually what settles it. */
  function wish(n) {
    return n <= 2 ? 49 : n <= 5 ? 45 : n === 6 ? 41 : n <= 7 ? 34 : Math.max(20, 34 * Math.sqrt(7 / n));
  }
  /* how close two centres may get, as a fraction of the cluster size */
  function slack(n) { return n <= 4 ? 0.8 : n <= 7 ? 0.72 : 0.64; }
  var SPANS = [110, 140, 170, 200, 225, 245, 265, 285, 305, 325];
  function PREF(n) {
    return n <= 1 ? 150 : n === 2 ? 200 : n === 3 ? 215 : n === 4 ? 240 : n === 5 ? 255 : n === 6 ? 270 : n === 7 ? 280 : 310;
  }

  /* ---- where the composition may actually go ----
     The scene has no frame, no card and no panel: it stands on the hero's flat
     ground. So the stage rectangle is not an edge, it is only a coordinate
     system, and a burst that runs past it reads as a full-bleed hero rather
     than a mistake. The real edges are measured off the live page each render,
     in the same w-units the solver works in:
       l  the viewport's left edge. Soft — a burst may bleed past it and be
          cropped, which is the whole point of a bleeding composition.
       r  the buy panel. HARD. Nothing may reach it, ever.
       t  the eyebrow sitting above the scene.
       b  whatever is under the scene — the buy panel on a phone, otherwise the
          bottom of the viewport, because the composition must not push the
          page into a scroll to be seen whole. */
  var PANEL_GAP = 22; /* px of clear air kept off the buy panel      */
  var PAGE_GAP = 8;   /* px of clear air kept off the viewport bottom */
  var CROP = 0.22;    /* how much of a burst the left edge may crop   */

  /* Slots run left → bottom → right, so the first refill keeps its side of the
     case as the others arrive instead of hopping across the stage. */
  function angles(n, span) {
    var out = [];
    for (var i = 0; i < n; i++) {
      var t = n === 1 ? 0 : i / (n - 1); /* a lone cluster sits to one side */
      out.push(((180 + span / 2 - t * span) * Math.PI) / 180);
    }
    return out;
  }
  function place(ang, r) {
    return ang.map(function (a) {
      return { x: 50 + r * Math.sin(a), y: RING_CY - r * SQUASH * Math.cos(a) };
    });
  }
  function caseBox(H, nc) {
    var h = (CASE_H / 100) * H;
    var f = fanOf(nc || 1);
    /* half-width of the whole fan: one scaled case, plus the spread of the rest */
    var half = (h * CASE_AR * f.scale) / 2 + ((nc || 1) - 1) * ((f.gap / 100) * h) / 2;
    return { half: half, top: (CASE_TOP / 100) * H, bot: ((CASE_TOP + CASE_H) / 100) * H };
  }
  /* signed distance from a cluster's centre to the case silhouette: positive
     outside it, negative once the centre is over the case itself */
  function sdCase(p, C) {
    var dx = Math.max(50 - C.half - p.x, p.x - (50 + C.half));
    var dy = Math.max(C.top - p.y, p.y - C.bot);
    return Math.hypot(Math.max(dx, 0), Math.max(dy, 0)) + Math.min(Math.max(dx, dy), 0);
  }
  /* the bite: how deep this cluster reaches into the case, as a fraction of the
     cluster's own width. 0 = clear of it, 0.10 = a tenth of the cluster behind. */
  function bite(p, vr, C) { return Math.max(0, vr - sdCase(p, C)) / (2 * vr); }
  /* how much of this cluster's pod the case is covering */
  function podHidden(p, d, C) {
    var x0 = p.x - (POD_W * d) / 2, x1 = p.x + (POD_W * d) / 2;
    var y0 = p.y + POD_DY * d - (POD_H * d) / 2, y1 = p.y + POD_DY * d + (POD_H * d) / 2;
    var ox = Math.max(0, Math.min(x1, 50 + C.half) - Math.max(x0, 50 - C.half));
    var oy = Math.max(0, Math.min(y1, C.bot) - Math.max(y0, C.top));
    return (ox * oy) / ((x1 - x0) * (y1 - y0));
  }
  function fits(pts, d, C, B) {
    var vr = (d / 2) * ART, sep = d * slack(pts.length);
    /* the left edge is the one that gives: a burst may hang off it, cropped by
       up to CROP of its width, as long as its middle stays on the page */
    var left = B.l - 2 * vr * B.crop;
    for (var k = 0; k < pts.length; k++) {
      var p = pts[k];
      if (p.x - vr < left) return false;
      if (p.x + vr > B.r) return false; /* the panel. never. */
      if (p.y - vr < B.t || p.y + vr > B.b) return false;
      if (bite(p, vr, C) > BITE_MAX) return false; /* only ever a light touch */
      if (podHidden(p, d, C) > POD_HIDE) return false;
    }
    for (var i = 0; i < pts.length; i++) {
      for (var j = i + 1; j < pts.length; j++) {
        var a = pts[i], b = pts[j];
        var dist = Math.hypot(a.x - b.x, a.y - b.y);
        if (dist >= 2 * vr) continue; /* visually clear of each other */
        if (dist < sep) return false; /* the two bursts would read as one blob */
      }
    }
    return true;
  }
  /* The TIGHTEST ring that still fits, or null if this size can't be placed.
     Tight, not wide: the bite ceiling is what stops it closing in, so the ring
     it hands back is the one where the clusters sit nearest the case. Coarse
     sweep out from the middle for the first radius that works, then halve back
     into the gap for its exact edge — a flat crawl is the same answer for six
     times the work, and this runs on every click. */
  function radius(ang, d, C, B) {
    var hit = -1;
    for (var r = 6; r <= 58; r += 1) { if (fits(place(ang, r), d, C, B)) { hit = r; break; } }
    if (hit < 0) return null;
    var lo = hit - 1, hi = hit; /* lo fails, hi works */
    if (lo >= 6) {
      for (var i = 0; i < 6; i++) {
        var mid = (lo + hi) / 2;
        if (fits(place(ang, mid), d, C, B)) hi = mid; else lo = mid;
      }
    }
    return { r: hi, pts: place(ang, hi) };
  }
  /* An ellipse can't trace a tall thin case, so a ring alone leaves some
     clusters kissing and others hanging off in space. This walks each cluster
     back in along its own spoke until it reaches BITE_AIM, stopping the moment
     the bite ceiling, a neighbour or the stage edge says no. One step per
     cluster per round so they close in together rather than first-come. */
  function hug(pts, ang, d, C, B) {
    var vr = (d / 2) * ART;
    var out = pts.map(function (p) { return { x: p.x, y: p.y }; });
    var dir = ang.map(function (a) {
      var ux = Math.sin(a), uy = -SQUASH * Math.cos(a);
      var L = Math.hypot(ux, uy) || 1;
      return { x: ux / L, y: uy / L };
    });
    var done = out.map(function () { return false; });
    for (var round = 0; round < 90; round++) {
      var moved = false;
      for (var i = 0; i < out.length; i++) {
        if (done[i] || bite(out[i], vr, C) >= BITE_AIM) { done[i] = true; continue; }
        var step = { x: out[i].x - dir[i].x * 0.3, y: out[i].y - dir[i].y * 0.3 };
        var trial = out.map(function (p, j) { return j === i ? step : p; });
        if (!fits(trial, d, C, B)) { done[i] = true; continue; }
        out[i] = step;
        moved = true;
      }
      if (!moved) break;
    }
    return out;
  }
  function layout(n, H, B, cache, nc) {
    nc = nc || 1;
    var key = n + '@' + H.toFixed(2) + '@' + B.key + '@' + nc;
    if (cache.has(key)) return cache.get(key);
    var C = caseBox(H, nc);
    var win = null;
    /* adding a refill must never make the clusters bigger than they were at the
       count below, or the stepper grows and shrinks as you click through it */
    var cap = n > 1 ? Math.min(wish(n), layout(n - 1, H, B, cache, nc).d) : wish(n);
    for (var s = 0; s < SPANS.length; s++) {
      var span = SPANS[s];
      var ang = angles(n, span);
      var lo = 0, hi = cap, got = radius(ang, hi, C, B);
      if (got) lo = hi;
      else {
        for (var i = 0; i < 12; i++) {
          var mid = (lo + hi) / 2, g = radius(ang, mid, C, B);
          if (g) { lo = mid; got = g; } else hi = mid;
        }
      }
      if (!got) continue;
      var pts = hug(got.pts, ang, lo, C, B);
      var vr = (lo / 2) * ART;
      var touch = pts.filter(function (p) { return bite(p, vr, C) > 0.005; }).length;
      /* whole w-units, so a third of a percent of size never outvotes an arc
         that reads better or a cluster that actually reaches the case */
      var score = Math.round(lo);
      if (
        !win ||
        score > win.score ||
        (score === win.score && touch > win.touch) ||
        (score === win.score && touch === win.touch && Math.abs(span - PREF(n)) < Math.abs(win.span - PREF(n)))
      ) {
        win = { score: score, touch: touch, span: span, d: lo, pts: pts };
      }
    }
    if (!win) win = { score: 0, touch: 0, span: 325, d: 18, pts: place(angles(n, 325), 30) }; /* never nothing */
    cache.set(key, win);
    return win;
  }

  class BodiKitBuilder extends HTMLElement {
    connectedCallback() {
      var island = this.querySelector('[data-bkb-data]');
      if (!island) return;
      try { this.data = JSON.parse(island.textContent); } catch (e) { return; }
      if (!this.data.scents || !this.data.scents.length) return;

      this.money = (cents) => formatMoney(cents, this.data.moneyFormat);
      /* BASE_TARGET is per person; TARGET is what every counter, CTA and
         phantom-pricing site below reads. setPeople() recomputes TARGET, so
         none of those sites needed to learn about kit counts at all. */
      this.BASE_TARGET = this.data.target || 3;
      this.KITS_MAX = Math.max(1, Math.min(4, Number(this.data.kitsMax) || 4));
      this.TARGET = this.BASE_TARGET;

      /* The Blue case is selected on load. Nobody needs telling they can change
         the colour — the three tiles say so — and an empty stage was pure
         friction, so the scene is never unbuilt and the ghost-case state no
         longer exists. Liquid nominates the default; blue/sky by title is the
         fallback, then simply the first variant. */
      var cases = this.data.cases || [];
      var def = null;
      if (this.data.defaultCaseId != null) {
        def = cases.filter((c) => String(c.id) === String(this.data.defaultCaseId))[0] || null;
      }
      if (!def) def = cases.filter((c) => /blue|sky/i.test(c.title || ''))[0] || null;
      if (!def) def = cases[0] || null;

      /* The previewed scent — the one whose notes and blurb are showing above
         the grid — is its own piece of state, entirely separate from what is in
         the kit. Something is always previewed: Liquid nominates the first card
         and renders its profile server-side, so nothing flashes on load. */
      var firstScent = this.data.scents[0];
      var preview = this.data.previewScentId != null ? String(this.data.previewScentId) : null;
      if (!preview || !this.data.scents.some((s) => String(s.id) === preview)) preview = String(firstScent.id);

      var startCase = def || (this.data.cases && this.data.cases[0]) || null;
      var caseQty = {};
      if (startCase) caseQty[startCase.id] = 1;
      this.state = { caseQty: caseQty, kits: 1, casePreview: startCase ? String(startCase.id) : null, caseIncluded: true, qty: {}, plan: 'sub', status: 'idle', preview: preview };
      this.data.scents.forEach((s) => { this.state.qty[s.id] = 0; });

      this.$ = (n) => this.querySelector('[data-bkb="' + n + '"]');
      this.stage = this.$('stage');
      this.orbit = this.$('orbit');
      this.tags = this.$('tags');
      this.BOUND = { l: EDGE, r: 100 - EDGE, t: EDGE, b: 1e4, crop: 0, key: 'init' };
      this.layoutCache = new Map();

      this._wire();
      this._motion();
      this.render();

      if (this.querySelector('.mbar')) document.body.classList.add('bkb-mbar-on');

      var t;
      this._onResize = () => { clearTimeout(t); t = setTimeout(() => this.renderScene(), 160); };
      window.addEventListener('resize', this._onResize);
    }

    disconnectedCallback() {
      if (this._onResize) window.removeEventListener('resize', this._onResize);
      document.body.classList.remove('bkb-mbar-on');
    }

    /* ---------- helpers over state ---------- */
    scent(id) { return this.data.scents.filter((s) => String(s.id) === String(id))[0]; }
    chosen() { return this.data.scents.filter((s) => this.state.qty[s.id] > 0); }
    picked() { return this.data.scents.reduce((n, s) => n + this.state.qty[s.id], 0); }
    isSub() { return this.state.plan === 'sub'; }
    /* There is no cap. Prices scale with whatever is in the kit; until the
       target is reached we quote the target kit so the panel isn't pricing a
       half-built box. */
    phantom() { return Math.max(0, this.TARGET - this.picked()); }
    billed() { return Math.max(this.picked(), this.TARGET); }
    chosenValue() { return this.chosen().reduce((c, s) => c + s.price * this.state.qty[s.id], 0); }
    chosenSub() { return this.chosen().reduce((c, s) => c + s.sub * this.state.qty[s.id], 0); }
    refillValue() { return this.chosenValue() + this.phantom() * this.data.refillPrice; }
    refillSub() { return this.chosenSub() + this.phantom() * this.data.refillSubPrice; }
    /* The case the hero leads with: the colour you touched last, falling back to
       the first one in the kit. The scene draws every case, but the ground
       gradient can only be one colour, so it follows this. */
    kase() {
      var pv = this.state.casePreview;
      if (pv && this.state.caseQty[pv] > 0) return this.caseById(pv);
      var first = Object.keys(this.state.caseQty).filter((id) => this.state.caseQty[id] > 0)[0];
      return first ? this.caseById(first) : null;
    }
    caseById(id) { return this.data.cases.filter((c) => String(c.id) === String(id))[0] || null; }
    caseTotal() { return Object.keys(this.state.caseQty).reduce((n, id) => n + this.state.caseQty[id], 0); }
    /* Cart lines: two of one colour is one line of quantity 2. The BXGY discount
       counts units and not lines, so both cases still come free. */
    caseLines() {
      return Object.keys(this.state.caseQty)
        .filter((id) => this.state.caseQty[id] > 0)
        .map((id) => ({ kase: this.caseById(id), qty: this.state.caseQty[id] }))
        .filter((l) => l.kase);
    }
    /* Flat list, one entry per physical case, for drawing the scene. */
    caseFan() {
      var out = [];
      this.caseLines().forEach((l) => { for (var i = 0; i < l.qty; i++) out.push(l.kase); });
      return out;
    }
    casesValue() {
      return this.caseLines().reduce((c, l) => c + (l.kase ? l.kase.price : this.data.casePrice) * l.qty, 0);
    }
    bumpCase(id, delta) {
      var cur = this.state.caseQty[id] || 0;
      if (delta > 0 && this.caseTotal() >= this.state.kits) return; /* the kit count is the cap */
      this.state.caseQty[id] = Math.max(0, cur + delta);
      if (!this.state.caseQty[id]) delete this.state.caseQty[id];
      if (delta > 0) this.state.casePreview = String(id);
    }
    setKits(n) {
      n = Math.max(1, Math.min(this.KITS_MAX, n));
      this.state.kits = n;
      this.TARGET = this.BASE_TARGET * n;
      /* Top up with a colour nobody has yet, so "2 kits" lands on two different
         cases rather than two identical ones you cannot tell apart on a shelf.
         Repeats the first colour once the range runs out. */
      while (this.caseTotal() < n) {
        var used = Object.keys(this.state.caseQty).filter((id) => this.state.caseQty[id] > 0);
        var free = this.data.cases.filter((c) => used.indexOf(String(c.id)) === -1)[0] || this.data.cases[0];
        if (!free) break;
        this.state.caseQty[free.id] = (this.state.caseQty[free.id] || 0) + 1;
      }
      /* Coming back down, drop from the colours added last. */
      while (this.caseTotal() > n) {
        var ids = Object.keys(this.state.caseQty).filter((id) => this.state.caseQty[id] > 0);
        var last = ids[ids.length - 1];
        if (!last) break;
        this.state.caseQty[last] -= 1;
        if (!this.state.caseQty[last]) delete this.state.caseQty[last];
      }
    }
    casePrice() { var k = this.kase(); return k ? k.price : this.data.casePrice; }
    kitValue() { return (this.state.caseIncluded ? this.casesValue() : 0) + this.refillValue(); }
    kitTotal() {
      if (this.isSub()) return this.refillSub(); /* case is free on subscription */
      return (this.state.caseIncluded ? this.casesValue() : 0) + this.refillValue();
    }
    subTotal() { return this.refillSub(); }

    /* ---------- wiring ---------- */
    _wire() {
      /* Case tiles now work exactly like the scent cards: a stepper each, and
         the tile body only previews. One delegated listener decides which. */
      var cases = this.$('cases');
      if (cases) {
        cases.addEventListener('click', (e) => {
          var cell = e.target.closest('[data-case]');
          if (!cell) return;
          var id = cell.dataset.case;
          var ctrl = e.target.closest('[data-act]');
          this.state.caseIncluded = true;
          if (!ctrl) { this.state.casePreview = String(id); this.render(); return; }
          if (ctrl.disabled) return;
          this.bumpCase(id, ctrl.dataset.act === 'minus' ? -1 : 1);
          this.render();
        });
      }

      var kits = this.$('kits');
      if (kits) {
        kits.addEventListener('click', (e) => {
          var b = e.target.closest('[data-kits]');
          if (!b) return;
          this.setKits(Number(b.dataset.kits));
          this.render();
        });
      }

      /* Clicking a card does two completely different things depending on where
         it lands, and one delegated listener decides which — so a click can
         never fire both, and adding never scrolls or jumps.

           on a [data-act] control  Add, + or −. Moves the quantity, and previews
                                    that scent too, because you want to read what
                                    you just put in the box.
           anywhere else on a card  PREVIEW ONLY. Shows that scent's notes and
                                    blurb above the grid. Touches no quantity.

         No ceiling on quantity: take as many refills as you like. Three is only
         the kit's shape. */
      var grid = this.$('scentGrid');
      if (grid) {
        grid.addEventListener('click', (e) => {
          var card = e.target.closest('[data-scent]');
          if (!card) return;
          var id = card.dataset.scent;
          var ctrl = e.target.closest('[data-act]');
          if (!ctrl) { this.setPreview(id); return; } /* the card body: preview, nothing else */
          if (card.classList.contains('sold') || ctrl.disabled) return;
          if (ctrl.dataset.act === 'minus') this.state.qty[id] = Math.max(0, this.state.qty[id] - 1);
          else this.state.qty[id]++;
          this.state.preview = String(id);
          this.render();
        });
        /* The card is a real control on the keyboard too. The steppers inside it
           are buttons and handle their own Enter/Space, so only a keypress
           landing on the card itself previews. */
        grid.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
          var card = e.target.closest && e.target.closest('[data-scent]');
          if (!card || e.target !== card) return;
          e.preventDefault(); /* Space would otherwise scroll the page */
          this.setPreview(card.dataset.scent);
        });
      }

      var plans = this.$('plans');
      if (plans) {
        plans.addEventListener('click', (e) => {
          var btn = e.target.closest('[data-plan]');
          if (!btn) return;
          this.state.plan = btn.dataset.plan;
          this.render();
        });
      }

      /* Taking refills only drops the case from the box but KEEPS the colour you
         had, so putting it back is one click and never re-opens an empty state. */
      var skip = this.$('skipBtn');
      if (skip) {
        skip.addEventListener('click', (e) => {
          e.preventDefault();
          this.state.caseIncluded = false;
          this.render();
        });
      }
      var unskip = this.$('unskipBtn');
      if (unskip) {
        unskip.addEventListener('click', () => {
          this.state.caseIncluded = true;
          this.render();
          var c = this.$('cases');
          if (c) c.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      }

      var chev = this.$('chev');
      if (chev) {
        chev.addEventListener('click', () => {
          var open = this.$('sheet').classList.toggle('open');
          chev.setAttribute('aria-expanded', String(open));
        });
      }

      ['cta', 'ctaM'].forEach((n) => {
        var el = this.$(n);
        if (!el) return;
        el.addEventListener('click', () => this.submit());
      });
    }

    /* ---------- the scent profile ----------
       The same pair the live Refills product page prints above its swatches:
       the notes line in gold small caps, that scent's blurb underneath. Changing
       it is a paint, not a re-render — nothing about the kit, the scene or the
       price has moved, so none of that is touched. */
    setPreview(id) {
      if (String(this.state.preview) === String(id)) return;
      this.state.preview = String(id);
      this.paintPreview(true);
    }

    paintPreview(animate) {
      var cur = String(this.state.preview);
      this.querySelectorAll('[data-scent]').forEach((card) => {
        var on = card.dataset.scent === cur;
        card.classList.toggle('previewing', on);
        card.setAttribute('aria-pressed', String(on));
      });

      var box = this.$('profile');
      if (!box) return;
      var s = this.scent(cur);
      var notes = s ? s.notesFull || s.notes || '' : '';
      var blurb = s ? s.blurb || '' : '';
      var nEl = this.$('profileNotes');
      var bEl = this.$('profileBlurb');
      if (nEl) nEl.textContent = notes;
      if (bEl) bEl.textContent = blurb;
      var has = !!(notes || blurb);
      box.hidden = !has;
      if (has && animate) {
        box.classList.remove('is-revealing');
        void box.offsetWidth; /* restart the drop-in */
        box.classList.add('is-revealing');
      }
    }

    _motion() {
      var h1 = this.$('h1');
      if (h1) {
        h1.innerHTML = h1.textContent
          .split(' ')
          .map((w, i) => '<span class="word" style="--d:' + i * 150 + 'ms">' + esc(w) + '</span>')
          .join(' ');
      }
      if (!('IntersectionObserver' in window)) {
        this.querySelectorAll('.rise, .word').forEach((el) => el.classList.add('in'));
        return;
      }
      var io = new IntersectionObserver(
        (entries) => {
          entries.forEach((en) => {
            if (en.isIntersecting) { en.target.classList.add('in'); io.unobserve(en.target); }
          });
        },
        { threshold: 0.12 }
      );
      this.querySelectorAll('.rise, .word').forEach((el) => io.observe(el));
    }

    /* ---------- CTA ---------- */
    /* What the kit is still short of. The button names it rather than sitting
       there as a disabled grey slab: a visitor who can't see why it won't press
       assumes the page is broken, not that they have one more scent to choose. */
    scentsLeft() { return Math.max(0, this.TARGET - this.picked()); }
    /* Ordering two kits and only choosing one case is a half-built order.
       Refills-only skips the check entirely — there is no case to be short of. */
    casesLeft() { return this.state.caseIncluded ? Math.max(0, this.state.kits - this.caseTotal()) : 0; }

    complete() { return this.scentsLeft() === 0 && this.casesLeft() === 0; }

    ctaLabel() {
      if (this.state.status === 'loading') return 'Packing your kit';
      if (this.state.status === 'done') return 'In your cart. Nice one.';
      var scents = this.scentsLeft();
      if (scents === this.TARGET) return 'Pick your ' + this.TARGET + ' scents';
      /* "Add", not "Pick", because Add is the word on the button they have to
         press on each scent tile. */
      if (scents > 0) return 'Add ' + scents + ' more scent' + (scents > 1 ? 's' : '');
      var cases = this.casesLeft();
      if (cases > 0) return 'Add ' + cases + ' more case' + (cases > 1 ? 's' : '');
      return this.money(this.kitTotal()) + ' · ' + (this.isSub() ? 'Start my kit' : 'Add to cart');
    }

    ctaLabelShort() {
      if (this.state.status === 'loading') return 'Packing';
      if (this.state.status === 'done') return 'In your cart';
      var scents = this.scentsLeft();
      if (scents > 0) return scents === this.TARGET ? 'Pick ' + this.TARGET + ' scents' : 'Add ' + scents + ' more';
      var cases = this.casesLeft();
      if (cases > 0) return 'Add ' + cases + ' more case' + (cases > 1 ? 's' : '');
      return this.isSub() ? 'Start my kit' : 'Add to cart';
    }

    submit() {
      if (!this.complete() || this.state.status !== 'idle') return;

      var items = this.chosen().map((s) => {
        /* `_bundle` marks every kit line so Shopify Flow can tag the order and
           customer. The leading underscore hides it from cart and checkout. */
        var line = { id: Number(s.id), quantity: this.state.qty[s.id], properties: { _bundle: 'Starter Kit' } };
        /* Only the refills recur. */
        if (this.isSub() && this.data.sellingPlanId) line.selling_plan = Number(this.data.sellingPlanId);
        return line;
      });
      /* The case is deliberately a plain one-time line — no selling plan — so it
         never renews. The BXGY code below is what makes it free. */
      if (this.state.caseIncluded) {
        this.caseLines().forEach((l) => { if (l.kase) items.push({ id: Number(l.kase.id), quantity: l.qty, properties: { _bundle: 'Starter Kit' } }); });
      }
      if (!items.length) return;

      this.state.status = 'loading';
      this.render();

      fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items }),
      })
        .then((r) => { if (!r.ok) throw new Error('add failed'); return r.json(); })
        .then(() => {
          this.state.status = 'done';
          this.render();
          window.location.href = this._nextUrl();
        })
        .catch(() => {
          this.state.status = 'idle';
          this.render();
          var el = this.$('cta');
          if (el) el.textContent = 'Something went wrong. Try again.';
        });
    }

    _nextUrl() {
      var code = this.data.discountCode;
      var toCheckout = this.data.afterAdd === 'checkout';
      if (this.isSub() && code) {
        return toCheckout
          ? '/checkout?discount=' + encodeURIComponent(code)
          : '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent('/cart');
      }
      return toCheckout ? '/checkout' : '/cart';
    }

    /* ---------- summary lines ---------- */
    lineMarkup() {
      var html = '';
      if (this.state.caseIncluded) {
        var lines = this.caseLines();
        if (lines.length) {
          lines.forEach((l) => {
            var each = l.kase ? l.kase.price : this.data.casePrice;
            var right = this.isSub()
              ? '<span class="was">' + this.money(each * l.qty) + '</span><span class="free">Free</span>'
              : this.money(each * l.qty);
            var chip = 'background:linear-gradient(160deg,' + l.kase.tintB + ',' + l.kase.tintA + ')';
            html +=
              '<div class="line"><span class="l"><span class="sw" style="' + chip + '"></span><span class="t">' +
              esc(l.kase.title) + ' case' + (l.qty > 1 ? ' &times;' + l.qty : '') +
              '</span></span><span class="r">' + right + '</span></div>';
          });
        } else {
          var pendingRight = this.isSub()
            ? '<span class="was">' + this.money(this.data.casePrice) + '</span><span class="free">Free</span>'
            : this.money(this.data.casePrice);
          html +=
            '<div class="line pending"><span class="l"><span class="sw empty"></span><span class="t">Case, not picked yet</span></span><span class="r">' +
            pendingRight + '</span></div>';
        }
      }
      this.chosen().forEach((s) => {
        var q = this.state.qty[s.id];
        var right = this.isSub()
          ? '<span class="was">' + this.money(s.price * q) + '</span>' + this.money(s.sub * q)
          : this.money(s.price * q);
        var sw = s.image
          ? 'style="background-image:url(' + s.image + ');background-color:' + s.g1 + '"'
          : 'style="background:linear-gradient(140deg,hsl(38 34% 88%),hsl(43 52% 96%))"';
        html +=
          '<div class="line"><span class="l"><span class="sw" ' + sw + '></span><span class="t">' +
          esc(s.name) + ' refill' + (q > 1 ? ' × ' + q : '') + '</span></span><span class="r">' + right + '</span></div>';
      });
      for (var i = this.picked(); i < this.TARGET; i++) {
        var pr = this.isSub()
          ? '<span class="was">' + this.money(this.data.refillPrice) + '</span>' + this.money(this.data.refillSubPrice)
          : this.money(this.data.refillPrice);
        html +=
          '<div class="line pending"><span class="l"><span class="sw empty"></span><span class="t">Refill ' +
          (i + 1) + ', not picked yet</span></span><span class="r">' + pr + '</span></div>';
      }
      return html;
    }

    /* ---------- plan cards ---------- */
    planMarkup() {
      var withCase = this.state.caseIncluded;
      var q = this.billed();
      var subNow = this.money(this.subTotal());
      var subWas = this.money(this.kitValue());
      var onceNow = this.money((withCase ? this.casePrice() : 0) + this.refillValue());

      var unit = this.money(this.data.refillSubPrice);
      var wasUnit = this.money(this.data.refillPrice);
      var every = this.data.intervalLabel || 'every 3 months';
      var subPerks = withCase
        ? ['Case free with your first kit, worth ' + this.money(this.casePrice()),
           'Refills drop to ' + unit + ' each, down from ' + wasUnit,
           'Skip, swap scents, pause or cancel anytime']
        : ['Refills drop to ' + unit + ' each, down from ' + wasUnit,
           word(q).replace(/^\w/, (c) => c.toUpperCase()) + ' refills land ' + every,
           'Skip, swap scents, pause or cancel anytime'];

      var subOn = this.isSub();
      return (
        '<button class="plan lite edge ' + (subOn ? 'edge-ink' : 'edge-forest sel') +
        '" data-plan="once" role="radio" aria-checked="' + (!subOn) + '" type="button">' +
        '<span class="plan-name">One-off</span>' +
        '<span class="plan-price"><span class="n">' + onceNow + '</span><span class="u">once</span></span>' +
        '</button>' +
        '<button class="plan edge ' + (subOn ? 'edge-gold sel' : 'edge-ink') +
        '" data-plan="sub" role="radio" aria-checked="' + subOn + '" type="button">' +
        '<span class="plan-flag">Best value</span>' +
        '<span class="plan-name">Subscribe &amp; Save</span>' +
        '<span class="plan-price"><span class="n">' + subNow + '</span><span class="w">' + subWas +
        '</span><span class="u">' + esc(every) + '</span></span>' +
        '<div class="perks">' + subPerks.map((p) => '<div>' + checkSVG + '<span>' + p + '</span></div>').join('') + '</div>' +
        '</button>'
      );
    }

    /* ---------- the scene ---------- */
    stageAR() {
      if (!this.stage) return 1.1;
      var ar = this.stage.offsetHeight / Math.max(1, this.stage.offsetWidth);
      return ar > 0.5 && ar < 2 ? ar : 1.1;
    }

    /* The real edges of the page, in w-units, read fresh on every render. */
    measureBounds() {
      var B = this.BOUND;
      if (!this.stage) return;
      var st = this.stage.getBoundingClientRect();
      if (!st.width || !st.height) return;
      var u = (px) => (px / st.width) * 100; /* px → w-units */
      var H = u(st.height);
      var vw = document.documentElement.clientWidth, vh = window.innerHeight;
      var buy = this.querySelector('.buy');
      var b = buy ? buy.getBoundingClientRect() : null;
      /* is the panel beside the scene, or stacked under it? */
      var beside = !!b && b.top < st.bottom - 1 && b.bottom > st.top + 1;

      B.l = u(-st.left);
      B.r = beside ? u(b.left - PANEL_GAP - st.left) : u(vw - st.left);
      /* the eyebrow used to set the top bound; with it removed the scene runs
         to the top of its own stage */
      B.t = 0;
      var floor = beside ? vh - PAGE_GAP : Math.min(vh - PAGE_GAP, b ? b.top - PANEL_GAP : vh);
      /* never spill so far below the scene that it starts eating the next
         section — the rectangle is artificial, but it is still the composition */
      B.b = Math.min(u(floor - st.top), H + 9);
      B.crop = CROP;
      B.key = [B.l, B.r, B.t, B.b].map((v) => v.toFixed(1)).join(',');
    }

    /* every refill in the kit, in card order, one entry each */
    units() {
      var out = [];
      this.data.scents.forEach((s) => {
        var q = this.state.qty[s.id] || 0;
        for (var i = 0; i < q; i++) out.push({ scent: s, key: s.id + '#' + i });
      });
      return out;
    }

    renderScene() {
      if (!this.stage || !this.orbit || !this.tags) return;
      var fan = this.state.caseIncluded ? this.caseFan() : [];
      var showCase = fan.length > 0;

      /* --- the cases in the middle: one per kit, fanned from the centre ---
         Keyed on the colours in play so re-rendering for any other reason (a
         scent, a plan switch) never re-triggers the landing animation. */
      var cage = this.querySelector('.stage-case');
      if (cage) {
        var key = fan.map((k) => k.id).join(',');
        if (cage.dataset.fan !== key) {
          cage.dataset.fan = key;
          if (!fan.length) {
            cage.innerHTML = '';
          } else {
            var f = fanOf(fan.length);
            cage.innerHTML = fan
              .map((k, i) => {
                var off = (i - (fan.length - 1) / 2) * f.gap;
                /* the outer ones sit behind, so the fan reads with depth */
                var z = 10 - Math.abs(i - (fan.length - 1) / 2);
                return (
                  '<span class="case-slot" style="transform:translateX(' + off.toFixed(2) +
                  '%) scale(' + f.scale + ');z-index:' + z.toFixed(0) + '">' +
                  '<img class="case-shot on" alt="" aria-hidden="true" width="900" height="900" src="' +
                  k.image + '" style="animation-delay:' + (i * 70) + 'ms">' +
                  '</span>'
                );
              })
              .join('');
          }
        }
        this.stage.classList.toggle('has-case', showCase);
      }

      /* --- one cluster per refill: pod in front, that scent's botanicals behind --- */
      this.measureBounds();
      var list = this.units(), n = list.length;
      var L = layout(n, 100 * this.stageAR(), this.BOUND, this.layoutCache, this.state.caseIncluded ? Math.max(1, this.caseTotal()) : 1);
      var live = {};
      list.forEach((u) => { live[u.key] = true; });
      var pc = (v) => ((v / BOX) * 100).toFixed(2) + '%'; /* w-units → box % */

      [this.orbit, this.tags].forEach((layer) => {
        Array.prototype.slice.call(layer.children).forEach((el) => {
          if (el.classList.contains('out')) return;
          if (!live[el.dataset.sat]) {
            el.classList.add('out');
            setTimeout(() => el.remove(), 340);
          }
        });
      });

      list.forEach((u, i) => {
        var sel = '[data-sat="' + (window.CSS && CSS.escape ? CSS.escape(u.key) : u.key) + '"]:not(.out)';
        var el = this.orbit.querySelector(sel);
        if (!el) {
          el = document.createElement('div');
          el.className = 'sat';
          el.dataset.sat = u.key;
          el.innerHTML =
            '<span class="sat-in"><span class="sat-drift">' +
            (u.scent.image ? '<img class="sat-img" src="' + u.scent.image + '" alt="">' : '') +
            (this.data.podImage ? '<img class="sat-pod" src="' + this.data.podImage + '" alt="">' : '') +
            '</span></span>';
          this.orbit.appendChild(el);
        }
        var tag = this.tags.querySelector(sel);
        if (!tag) {
          tag = document.createElement('div');
          tag.className = 'tag-anchor';
          tag.dataset.sat = u.key;
          tag.innerHTML = '<span class="sat-tag">' + esc(u.scent.name) + '</span>';
          this.tags.appendChild(tag);
        }
        /* w-units → a transform on a box that never changes size, so a re-lay
           costs the compositor and nothing else, however many are on stage */
        var p = L.pts[i] || { x: 50, y: 60 };
        var tx = pc(p.x - BOX / 2), ty = pc(p.y - BOX / 2);
        el.style.setProperty('--fd', (i * 0.8).toFixed(2) + 's');
        el.style.setProperty('--k', (L.d / BOX).toFixed(4));
        el.style.setProperty('--tx', tx);
        el.style.setProperty('--ty', ty);
        /* painter's order: whatever sits lower on the stage sits in front */
        el.style.zIndex = String(Math.round(p.y * 4));

        /* The name hangs off the outer flank, clear of the case in front of it —
           unless that flank has bled off the page or up against the panel, in
           which case it swaps to the inner side. A cropped burst is deliberate;
           a cropped name is just broken. */
        var off = p.x - 50;
        var side = Math.abs(off) < 4 ? 0 : off < 0 ? -1 : 1;
        var TAGW = 7, tagX = (s) => p.x + s * 0.22 * L.d;
        if (tagX(side) - TAGW < this.BOUND.l) side = 1;
        if (tagX(side) + TAGW > this.BOUND.r) side = -1;
        tag.style.setProperty('--tx', tx);
        tag.style.setProperty('--ty', ty);
        tag.style.setProperty('--tagx', pc(side * 0.22 * L.d));
        tag.style.setProperty('--tagy', pc(0.48 * L.d));
      });

      var sr = this.$('sceneSR');
      if (sr) {
        var said = list.map((u) => u.scent.name);
        sr.textContent =
          (showCase ? this.kase().title + ' case. ' : 'Refills only, no case. ') +
          (n ? said.join(', ') + (n === 1 ? ' refill in the scene.' : ' refills in the scene.') : 'No refills picked yet.');
      }
    }

    /* ---------- render ---------- */
    render() {
      var n = this.picked();
      var full = n >= this.TARGET;
      var sub = this.isSub();
      var set = (name, fn) => { var el = this.$(name); if (el) fn(el); };

      /* --- case cards --- */
      var atCap = this.caseTotal() >= this.state.kits;
      this.querySelectorAll('[data-case]').forEach((cell) => {
        var q = this.state.caseQty[cell.dataset.case] || 0;
        var tile = cell.querySelector('.case');
        cell.classList.toggle('picked', q > 0);
        if (tile) {
          tile.classList.toggle('sel', q > 0);
          tile.classList.toggle('edge-gold', q > 0);
          tile.classList.toggle('edge-ink', q === 0);
        }
        var qty = cell.querySelector('.qty');
        if (qty) qty.textContent = String(q);
        var minus = cell.querySelector('[data-act="minus"]');
        if (minus) minus.disabled = q === 0;
        /* Plus and Add close once the cases match the kits: you cannot buy a
           third case on a two-kit order, and the cap is where you learn that. */
        cell.querySelectorAll('[data-act="plus"]').forEach((b) => { b.disabled = atCap; });
        var add = cell.querySelector('.add');
        if (add) add.hidden = q > 0;
        if (minus) minus.hidden = q === 0;
        var plusB = cell.querySelector('.bump[data-act="plus"]');
        if (plusB) plusB.hidden = q === 0;
      });
      set('caseBadge', (el) => {
        el.textContent = sub ? 'Free with Subscribe & Save' : this.money(this.casesValue()) + ' on a one-time order';
        el.classList.toggle('plain', !sub);
      });
      set('kits', (el) => {
        el.querySelectorAll('[data-kits]').forEach((b) => {
          var on = Number(b.dataset.kits) === this.state.kits;
          b.classList.toggle('on', on);
          b.setAttribute('aria-pressed', String(on));
        });
      });
      /* "Pick your case" reads wrong when you are picking two. Only touches a
         heading that actually ends in "case", so a custom one is left alone. */
      set('caseHeading', (el) => {
        var base = el.dataset.base || el.textContent;
        el.textContent = this.state.kits > 1 ? base.replace(/case$/i, 'cases') : base;
      });
      /* Spell out what the number means, so the count is never just a number. */
      set('kitsNote', (el) => {
        var k = this.state.kits;
        el.textContent = (this.state.caseIncluded ? (k === 1 ? '1 case' : k + ' cases') + ', ' : '') + this.TARGET + ' refills';
      });

      /* --- what you get, and when --- */
      set('explain', (el) => {
        /* It describes the subscription, so on a one-time order it would be
           describing something that never happens. */
        el.hidden = !sub;
        if (!sub) return;
        var k = this.state.kits;
        var vals = {
          price: this.money(this.subTotal()),
          n: String(this.billed()),
          cases: this.state.caseIncluded ? (k === 1 ? 'case' : k + ' cases') : 'refills',
        };
        el.querySelectorAll('.what').forEach((w) => {
          var tpl = w.dataset.tpl || '';
          w.textContent = tpl.replace(/\{(price|n|cases)\}/g, (m, key) => vals[key]);
        });
      });
      set('skipNote', (el) => el.classList.toggle('on', !this.state.caseIncluded));
      set('skipRow', (el) => { el.style.display = this.state.caseIncluded ? '' : 'none'; });

      /* --- scents --- */
      this.querySelectorAll('[data-scent]').forEach((card) => {
        var q = this.state.qty[card.dataset.scent] || 0;
        card.classList.toggle('picked', q > 0);
        card.classList.toggle('edge-gold', q > 0);
        var ctrl = card.querySelector('[data-ctrl]');
        if (!ctrl) return;
        var qty = ctrl.querySelector('.qty');
        if (qty) { qty.textContent = q; qty.style.display = q > 0 ? '' : 'none'; }
        ctrl.querySelectorAll('.bump').forEach((el) => { el.style.display = q > 0 ? '' : 'none'; });
        var add = ctrl.querySelector('.add');
        if (add) add.style.display = q > 0 ? 'none' : '';
      });
      /* which card is being read about — a different thing from what's in the
         kit, and drawn differently (forest ring vs gold) */
      this.paintPreview(false);
      set('counter', (el) => {
        el.textContent = n > this.TARGET ? n + ' picked' : n + ' of ' + this.TARGET + ' picked';
        el.classList.toggle('full', full);
      });

      /* --- plans --- */
      set('plans', (el) => { el.innerHTML = this.planMarkup(); });

      /* --- ground colour (hero + mobile bar) --- */
      this.style.setProperty('--bkb-ground-a', this.kase() ? this.kase().groundTop : 'hsl(213 10% 15%)');
      this.style.setProperty('--bkb-ground-b', this.kase() ? this.kase().groundBottom : 'hsl(213 8% 30%)');

      /* --- scene --- */
      this.renderScene();

      /* --- summary + totals --- */
      var html = this.lineMarkup();
      set('lines', (el) => { el.innerHTML = html; });
      set('linesM', (el) => { el.innerHTML = html; });

      var value = this.kitValue();
      var price = this.kitTotal();
      var save = value - price;
      var pct = value > 0 ? Math.round((save / value) * 100) : 0;

      ['valueTxt', 'valueTxtM'].forEach((k) =>
        set(k, (el) => { el.textContent = this.money(value); el.classList.toggle('strike', save > 0); })
      );
      set('priceTxt', (el) => { el.textContent = this.money(price); });
      set('topPrice', (el) => { el.textContent = this.money(price); });
      set('topWas', (el) => { el.textContent = this.money(value); el.style.display = save > 0 ? '' : 'none'; });
      set('topUnit', (el) => { el.textContent = sub ? this.data.intervalLabel : 'one-off'; });
      set('mPrice', (el) => { el.textContent = this.money(price); });
      set('mWas', (el) => { el.textContent = this.money(value); el.style.display = save > 0 ? '' : 'none'; });
      set('perTxt', (el) => { el.textContent = sub ? this.data.intervalLabel : 'one-off'; });
      set('kitKind', (el) => { el.textContent = sub ? this.data.planNoun : 'One-off'; });

      /* savings chip, or a nudge back to the subscription when there's nothing to save */
      set('saveSlot', (el) => {
        el.innerHTML = save > 0
          ? '<span class="savechip">You save ' + this.money(save) + ' (' + pct + '%)</span>'
          : '<button class="savechip" type="button" data-switch>Subscribe and save ' + this.money(value - this.subTotal()) + '</button>';
        var sw = el.querySelector('[data-switch]');
        if (sw) sw.addEventListener('click', () => { this.state.plan = 'sub'; this.render(); });
      });

      set('mStatus', (el) => {
        el.textContent =
          (n > this.TARGET ? n + ' scents' : n + ' of ' + this.TARGET + ' scents') +
          (sub ? ' · ' + String(this.data.planNoun || 'Quarterly').toLowerCase() : ' · one-off');
      });

      /* --- renewal + reassurance --- */
      set('renew', (el) => {
        /* On a subscription the "what you get, and when" block below already
           states the renewal, with the figure and the timing. Saying it twice
           in two different shapes read as two different charges. */
        el.textContent = sub ? '' : 'One box. Nothing recurring, nothing to cancel.';
        el.hidden = sub;
      });
      set('a1', (el) => {
        el.textContent = !this.state.caseIncluded
          ? 'Refills only, no case in this box'
          : sub ? 'Free case worth ' + this.money(this.casePrice()) : 'Case charged at ' + this.money(this.casePrice());
      });
      set('a2', (el) => { el.textContent = sub ? 'Skip, pause or cancel anytime' : 'No subscription, nothing recurring'; });
      set('a3', (el) => { el.textContent = sub ? 'Refills delivered ' + this.data.intervalLabel : 'Switch to a plan whenever you like'; });

      /* --- CTA --- */
      [['cta', this.ctaLabel()], ['ctaM', this.ctaLabelShort()]].forEach((pair) => {
        set(pair[0], (el) => {
          el.textContent = pair[1];
          el.disabled = this.state.status === 'idle' ? !this.complete() : this.state.status === 'loading';
          el.classList.toggle('loading', this.state.status === 'loading');
          el.classList.toggle('done', this.state.status === 'done');
        });
      });
    }
  }

  customElements.define('bodi-kit-builder', BodiKitBuilder);
})();
